import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography, Stack } from "@mui/material";
import Link from "@mui/material/Link";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import SearchInput from "../components/SearchInput";
import DataTableGeneric from "../components/DataTableGeneric";
import { routes } from "../routes/routes";
import useFetchAspects from "../hooks/useFetchAspects";
import useToggleUserActive from "../hooks/useToggleUserActive";
import useImportAspectsFromExcel from "../hooks/useImportAspectsFromExcel";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import DescriptionIcon from "@mui/icons-material/Description";
import BulkLoadModal from "../components/Modal/BulkLoadModal";
import BulkLoadResultsModal from "../components/Modal/BulkLoadResultsModal";
import EditAspectModal from "../components/Modal/EditAspectModal";
import useUpdateAspect from "../hooks/useUpdateAspect";
import useDeleteAspect from "../hooks/useDeleteAspect";
import ConfirmationModal from "../components/Modal/ConfirmationModal";

const AspectsPage = () => {
  const { data: aspects, error: responseError, loading, refetch: refetchAspects } = useFetchAspects();
  const { importFromExcel, loading: importLoading } = useImportAspectsFromExcel();
  const [rows, setRows] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelResult, setExcelResult] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAspect, setSelectedAspect] = useState(null);
  const { updateAspect, error: updateError } = useUpdateAspect();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [aspectToDelete, setAspectToDelete] = useState(null);
  const { deleteAspect, loading: deleting, error: deleteError } = useDeleteAspect();
  const [deletingId, setDeletingId] = useState(null);
  const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_aspectos_plantilla.xlsx`;

  useEffect(() => {
    const err = responseError || updateError || deleteError;
    if (err) {
      setErrorMessage(err?.message || "Ha ocurrido un error inesperado.");
      setShowError(true);
    }
  }, [responseError, updateError, deleteError]);

  useEffect(() => {
    if (aspects && Array.isArray(aspects)) {
      const newRows = aspects.map((aspect) => {
        return {
          id: aspect.id,
          external_id: aspect.external_id || "",
          name: aspect.name || "",
          description: aspect.description || "",
        };
      });
      setRows(newRows);
    }
  }, [aspects]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleImportFromExcel = async (file) => {
    const result = await importFromExcel(file);
    // Errores de backend a nivel general
    if (result?.errorMessage) {
      setErrorMessage(result.errorMessage.message || "Hubo un error al procesar el archivo.");
      setShowError(true);
      return false;
    }
    // Errores específicos del archivo
    if (result?.data?.errors) {
      const encabezadoError = result.data.errors.find((e) => e.includes("Encabezado incorrecto"));
      if (encabezadoError) {
        setErrorMessage("El archivo no contiene la estructura correcta.");
        setShowError(true);
        return false;
      }
    }

    if (result?.data && !result?.data?.errors) {
      setExcelResult(result.data);
      setShowExcelModal(true);
      return true;
    }

    setErrorMessage("No se pudo procesar el archivo.");
    setShowError(true);
    return false;
  };

  const filteredAspects = useMemo(
    () =>
      rows.filter(
        (aspect) =>
          (aspect.name || "").toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
          (aspect.description && aspect.description.toLowerCase().includes(searchQuery.trim().toLowerCase())),
      ),
    [rows, searchQuery],
  );

  const breadcrumbs = [
    <Link underline="none" key="1" color="inherit" href={routes.root}>
      Página Principal
    </Link>,
    <Typography key="3" color="secondary" sx={{ fontSize: "14px" }}>
      Aspectos
    </Typography>,
  ];

  const columns = [
    { id: "external_id", label: "ID Externo", minWidth: 150 },
    { id: "name", label: "Nombre", minWidth: 170 },
    { id: "description", label: "Descripción", minWidth: 200 },
    {
      id: "actions",
      label: "Acciones",
      minWidth: 220,
      align: "right",
    },
  ];

  const askDelete = (row) => {
    setAspectToDelete(row);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!aspectToDelete) return;
    try {
      setDeletingId(aspectToDelete.id);
      await deleteAspect(aspectToDelete.id);
      setRows((prev) => prev.filter((r) => r.id !== aspectToDelete.id));
      setSuccessMessage("Aspecto eliminado correctamente.");
      setShowSuccess(true);
    } catch (err) {
      const backendMsg = err?.backend?.message;
      setErrorMessage(backendMsg || err?.message || "No se pudo eliminar el aspecto.");
      setShowError(true);
    } finally {
      setDeletingId(null);
      setShowDeleteModal(false);
      setAspectToDelete(null);
    }
  };

  const tableRows = useMemo(() => {
    return filteredAspects.map((r) => {
      const isDeletingThis = deleting && deletingId === r.id;

      return {
        ...r,
        actions: (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setSelectedAspect(r);
                setShowEditModal(true);
              }}
            >
              Editar
            </Button>

            <Button variant="outlined" color="error" size="small" disabled={isDeletingThis} onClick={() => askDelete(r)}>
              {isDeletingThis ? "Eliminando..." : "Eliminar"}
            </Button>
          </Stack>
        ),
      };
    });
  }, [filteredAspects, deleting, deletingId]);

  const handleOpenImportModal = () => {
    setShowImportModal(true);
  };

  const handleCloseExcelResultModal = async () => {
    setShowExcelModal(false);

    if ((excelResult?.created ?? 0) > 0 && Array.isArray(excelResult?.newAspects)) {
      setRows((prevRows) => [...excelResult.newAspects, ...prevRows]);
    }

    if ((excelResult?.created ?? 0) > 0 || (excelResult?.updated ?? 0) > 0) {
      await refetchAspects();
    }

    setExcelResult(null);
  };

  const handleSubmitEdit = async ({ id, external_id, name, description }) => {
    const ok = await updateAspect({ id, external_id, name, description });
    if (!ok) {
      setErrorMessage("No se pudo actualizar el aspecto.");
      setShowError(true);
      return false;
    }

    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, external_id, name, description } : r)));
    setSuccessMessage("El aspecto fue actualizado correctamente.");
    setShowSuccess(true);
    return true;
  };

  return (
    <Box p={3}>
      <BreadcrumbsComponent
        breadcrumbs={[
          <Link underline="none" key="1" color="inherit" href={routes.root}>
            Página Principal
          </Link>,
          <Typography key="2" color="secondary" sx={{ fontSize: "14px" }}>
            Aspectos
          </Typography>,
        ]}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333333", mt: 2 }}>
          Listado de aspectos
        </Typography>

        <Box>
          <Button variant="contained" component="span" sx={{ mr: 2 }} disabled={importLoading} onClick={handleOpenImportModal}>
            {importLoading ? "Importando..." : "Importar Aspectos"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 2, mb: 3 }}>
        <SearchInput id="search-aspects" placeholder="Buscar por nombre o descripción..." value={searchQuery} onChange={handleSearchChange} />
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography align="center" color="textSecondary">
          No hay aspectos
        </Typography>
      ) : (
        <DataTableGeneric columns={columns} rows={tableRows} />
      )}

      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error" message={errorMessage} />
      <SuccessSnackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Completado"
        message={successMessage || "Operación realizada correctamente."}
      />

      {/* Modal de confirmación de borrado */}
      <ConfirmationModal
        open={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setAspectToDelete(null);
        }}
        handleConfirm={handleConfirmDelete}
        title="Eliminar aspecto"
        description={aspectToDelete ? `¿Está seguro de que desea eliminar el aspecto "${aspectToDelete.name}"?` : "Esta acción no se puede deshacer."}
        confirmText={deleting ? "Eliminando..." : "Eliminar"}
        confirmColor="error"
      />

      {/* Modal de resultado de la importación */}
      <BulkLoadResultsModal
        open={showExcelModal}
        onClose={handleCloseExcelResultModal}
        result={excelResult}
        title="Resultado de importación"
        labels={{
          total: "Total aspectos",
          created: "Creados",
          updated: "Actualizados",
          failed: "Errores",
          skipped: "Sin cambios",
        }}
        keys={{
          total: "total",
          created: "created",
          updated: "updated",
          failed: "failed",
          errorDetails: "error_details",
          skipped: "skipped",
        }}
      />

      {/* Modal para importar aspectos por Excel */}
      <BulkLoadModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSelectFile={handleImportFromExcel}
        title="Importar aspectos por Excel"
        description="Puede descargar la plantilla requerida para la importación de aspectos y cargarla desde su equipo."
        template={{ url: TEMPLATE_URL, label: "Descargar plantilla" }}
        accept=".xlsx,.xls"
        maxSizeMB={10}
        uploadButtonLabel="Subir y crear"
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={["Asegúrese de completar todos los campos obligatorios en la plantilla."]}
        closeOnSuccess={true}
      />

      {/* Modal de edición */}
      <EditAspectModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAspect(null);
        }}
        initialData={selectedAspect}
        onSubmit={handleSubmitEdit}
      />
    </Box>
  );
};

export default AspectsPage;
